import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { rupee, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useSiteStatus } from "../context/SiteStatusContext";
import { toast } from "sonner";
import { Tag, Check, User, MapPin, CreditCard, Wallet, Sparkles, AlertTriangle, Truck, Store, Clock, Plus, MessageSquare } from "lucide-react";
import { trackBeginCheckout } from "../lib/analytics";

const PRESET_REQUESTS = ["Extra spicy", "Less spicy", "No onions", "No garlic", "Less oil", "Extra raita", "Cutlery needed", "Ring the bell"];

export default function Checkout() {
  const { items, subtotal, clearCart } = useCart();
  const { user } = useAuth();
  const { status } = useSiteStatus();
  const navigate = useNavigate();
  const ordersStopped = !!status?.stop_orders;
  const paymentsStopped = !!status?.stop_online_payments;

  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState({ name: user?.name || "", phone: user?.phone || "", email: user?.email || "" });
  const [orderType, setOrderType] = useState("delivery"); // delivery | takeaway
  const [address, setAddress] = useState({ label: "Home", address: "", landmark: "", city: localStorage.getItem("bbs_location") || "", pincode: "", instructions: "" });
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [useNewAddr, setUseNewAddr] = useState(false);
  const [deliveryAreas, setDeliveryAreas] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [kitchenId, setKitchenId] = useState("");
  const [coupon, setCoupon] = useState("");
  const [applied, setApplied] = useState(null);
  const [payMethod, setPayMethod] = useState("online");
  const [placing, setPlacing] = useState(false);
  const [redeem, setRedeem] = useState(false);
  const [specialInstructions, setSpecialInstructions] = useState("");

  const togglePreset = (label) => {
    setSpecialInstructions((prev) => {
      const parts = prev.split(",").map((p) => p.trim()).filter(Boolean);
      const idx = parts.findIndex((p) => p.toLowerCase() === label.toLowerCase());
      if (idx >= 0) parts.splice(idx, 1); else parts.push(label);
      return parts.join(", ");
    });
  };
  const presetActive = (label) =>
    specialInstructions.split(",").map((p) => p.trim().toLowerCase()).includes(label.toLowerCase());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (items.length === 0) navigate("/menu"); }, []);
  // begin_checkout — fires once when the checkout page opens with items
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (items.length > 0) trackBeginCheckout(subtotal); }, []);
  useEffect(() => { if (paymentsStopped) setPayMethod("cod"); }, [paymentsStopped]);
  useEffect(() => {
    if (user) setCustomer((c) => ({ name: c.name || user.name || "", phone: c.phone || user.phone || "", email: c.email || user.email || "" }));
  }, [user?.id]);

  useEffect(() => {
    api.get("/kitchens").then((r) => setKitchens(r.data)).catch(() => {});
    api.get("/delivery-areas").then((r) => setDeliveryAreas(r.data)).catch(() => {});
    if (user) api.get("/my/addresses").then((r) => {
      setSavedAddresses(r.data);
      setUseNewAddr(r.data.length === 0);
      const def = r.data.find((a) => a.is_default) || r.data[0];
      if (def) setAddress((prev) => (prev.__id ? prev : { ...def, __id: def.id, instructions: prev.instructions || "" }));
    }).catch(() => setUseNewAddr(true));
    else setUseNewAddr(true);
    // Reorder prefill
    try {
      const pf = JSON.parse(localStorage.getItem("bbs_reorder") || "null");
      if (pf) {
        if (pf.order_type) setOrderType(pf.order_type);
        if (pf.order_type === "takeaway" && pf.kitchen_id) setKitchenId(pf.kitchen_id);
        if (pf.delivery_address) { setAddress({ ...address, ...pf.delivery_address }); setUseNewAddr(true); }
        localStorage.removeItem("bbs_reorder");
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const selectedKitchen = kitchens.find((k) => k.id === kitchenId) || null;

  // Serviceability is pincode-first: any pincode listed under an active area passes,
  // else fall back to a matching active city.
  const activeAreas = deliveryAreas.filter((a) => a.active);
  const pin = (address.pincode || "").trim();
  const cityL = (address.city || "").trim().toLowerCase();
  const matchArea = (pin && activeAreas.find((a) => (a.pincodes || []).map(String).map((p) => p.trim()).includes(pin)))
    || (cityL && activeAreas.find((a) => (a.city || "").trim().toLowerCase() === cityL))
    || null;
  const pinEntered = pin.length >= 6;
  const serviceable = orderType !== "delivery" || !!matchArea;
  const notServiceable = orderType === "delivery" && pinEntered && !matchArea;

  const availablePoints = user?.loyalty_points || 0;
  const discount = applied?.discount || 0;
  const afterDiscount = Math.max(subtotal - discount, 0);
  const maxRedeem = Math.min(availablePoints, Math.floor(afterDiscount * 0.2), 200);
  const pointsApplied = redeem ? maxRedeem : 0;
  const taxable = Math.max(afterDiscount - pointsApplied, 0);
  const tax = Math.round(taxable * 0.05);
  const freeAbove = matchArea?.free_delivery_above ?? 999;
  const areaFee = matchArea?.delivery_fee ?? 40;
  const delivery = orderType === "takeaway" ? 0 : (subtotal >= freeAbove ? 0 : areaFee);
  const total = taxable + tax + delivery;
  const pointsEarn = Math.round(total * 0.05);

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, subtotal });
      setApplied(data);
      toast.success(`Coupon ${data.code} applied — saved ${rupee(data.discount)}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setApplied(null); }
  };

  const buildItems = () => items.map((i) => ({
    product_id: i.product_id, name: i.name, variant: i.variant, quantity: i.quantity,
    customizations: i.customizations, add_ons: i.add_ons,
  }));

  const loadRazorpay = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  const payWithRazorpay = async (order) => {
    const { data: rzp } = await api.post("/payments/razorpay/order", { order_id: order.id });
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Could not load Razorpay. Please try again."); setPlacing(false); return; }
    const rz = new window.Razorpay({
      key: rzp.key_id, amount: rzp.amount, currency: rzp.currency, order_id: rzp.razorpay_order_id,
      name: "Begum's Biryani Secrets", description: `Order ${order.order_number}`,
      prefill: { name: customer.name, email: customer.email || "", contact: customer.phone },
      theme: { color: "#0B2545" },
      handler: async (resp) => {
        try {
          await api.post("/payments/razorpay/verify", {
            order_id: order.id, razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature,
          });
          clearCart();
          toast.success("Payment successful!");
          navigate(`/order-confirmation/${order.order_number}`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPlacing(false); }
      },
      modal: { ondismiss: () => { toast.message("Payment cancelled"); setPlacing(false); } },
    });
    rz.on("payment.failed", (r) => { toast.error(r.error?.description || "Payment failed"); setPlacing(false); });
    rz.open();
  };

  const placeOrder = async () => {
    if (ordersStopped) { toast.error("We are not taking orders at the moment. Please check back soon."); return; }
    if (!customer.name || !customer.phone) { toast.error("Please enter name and mobile"); setStep(1); return; }
    if (orderType === "delivery" && (!address.address || !address.city || !address.pincode)) { toast.error("Please complete delivery address"); setStep(2); return; }
    if (notServiceable) { toast.error("We don't deliver to this pincode yet. Please choose a serviceable address or Operating Kitchen (pickup)."); setStep(2); return; }
    if (orderType === "takeaway" && !kitchenId) { toast.error("Please select an Operating Kitchen for pickup"); setStep(2); return; }
    setPlacing(true);
    try {
      const payload = {
        items: buildItems(), coupon_code: applied?.code || null,
        customer, payment_method: payMethod, redeem_points: pointsApplied,
        order_type: orderType,
        special_instructions: specialInstructions.trim() || null,
      };
      if (orderType === "takeaway") payload.kitchen_id = kitchenId;
      else payload.delivery_address = address;
      const { data: order } = await api.post("/orders", payload);
      if (payMethod === "cod") {
        clearCart();
        toast.success("Order placed!");
        navigate(`/order-confirmation/${order.order_number}`);
        return;
      }
      await payWithRazorpay(order);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPlacing(false);
    }
  };

  const steps = [["Customer", User], ["Address", MapPin], ["Summary", Tag], ["Payment", CreditCard]];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 lg:py-12" data-testid="checkout-page">
      <h1 className="font-serif text-4xl text-navy mb-6">Checkout</h1>
      {ordersStopped && (
        <div data-testid="checkout-orders-stopped" className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3.5 text-sm">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <span>We are currently closed and not taking orders. Please check back soon or WhatsApp us for urgent queries.</span>
        </div>
      )}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto scrollbar-hide">
        {steps.map(([label, Icon], i) => (
          <React.Fragment key={i}>
            <button onClick={() => setStep(i + 1)} className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-sm shrink-0 transition-colors ${step === i + 1 ? "bg-navy text-cream" : step > i + 1 ? "bg-gold/15 text-navy" : "bg-white border border-black/10 text-muted-foreground"}`}>
              <Icon size={15} /> {label}
            </button>
            {i < 3 && <div className="h-px w-4 bg-black/15 shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
        <div className="lg:col-span-7 space-y-4">
          {step === 1 && (
            <Card title="Customer Details" testid="step-customer">
              <Field label="Full Name" value={customer.name} onChange={(v) => setCustomer({ ...customer, name: v })} testid="cust-name" />
              <Field label="Mobile Number" value={customer.phone} onChange={(v) => setCustomer({ ...customer, phone: v })} testid="cust-phone" />
              <Field label="Email (optional)" value={customer.email} onChange={(v) => setCustomer({ ...customer, email: v })} testid="cust-email" />
              <button onClick={() => setStep(2)} data-testid="to-delivery-btn" className="mt-2 bg-navy text-cream font-semibold px-6 py-3 rounded-full hover:bg-navy-soft transition-colors active:scale-95">Continue</button>
            </Card>
          )}

          {step === 2 && (
            <Card title="How would you like it?" testid="step-delivery">
              {/* Segmented toggle */}
              <div className="flex bg-white border border-black/10 rounded-full p-1" data-testid="fulfilment-toggle">
                <button onClick={() => setOrderType("delivery")} data-testid="delivery-toggle" className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold transition-all ${orderType === "delivery" ? "bg-navy text-cream" : "text-navy/70"}`}><Truck size={16} /> Delivery</button>
                <button onClick={() => setOrderType("takeaway")} data-testid="pickup-toggle" className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-full text-sm font-semibold transition-all ${orderType === "takeaway" ? "bg-navy text-cream" : "text-navy/70"}`}><Store size={16} /> Operating Kitchen</button>
              </div>

              {orderType === "delivery" ? (
                <div className="mt-4 space-y-3" data-testid="delivery-section">
                  {savedAddresses.length > 0 && !useNewAddr && (
                    <div className="space-y-3">
                      {savedAddresses.map((a) => {
                        const sel = address.__id === a.id;
                        return (
                          <button key={a.id} onClick={() => setAddress({ ...a, __id: a.id, instructions: address.instructions || "" })} data-testid={`saved-address-${a.id}`}
                            className={`w-full text-left flex items-start gap-3 p-4 rounded-lg border transition-all ${sel ? "border-gold bg-gold/5" : "border-black/10 hover:border-navy/30"}`}>
                            <MapPin size={16} className="text-gold mt-0.5 shrink-0" />
                            <div className="flex-1"><div className="font-medium text-navy text-sm">{a.label}</div><div className="text-sm text-muted-foreground">{a.address}{a.landmark ? `, ${a.landmark}` : ""}, {a.city} - {a.pincode}</div></div>
                            {sel && <Check className="text-gold shrink-0" size={18} />}
                          </button>
                        );
                      })}
                      <button onClick={() => { setUseNewAddr(true); setAddress({ label: "Home", address: "", landmark: "", city: localStorage.getItem("bbs_location") || "", pincode: "", instructions: "" }); }} data-testid="use-new-address" className="flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-gold"><Plus size={15} /> Use a new address</button>
                    </div>
                  )}
                  {(useNewAddr || savedAddresses.length === 0) && (
                    <div className="space-y-3">
                      <Field label="Address" value={address.address} onChange={(v) => setAddress({ ...address, address: v, __id: undefined })} testid="addr-line" />
                      <Field label="Landmark (optional)" value={address.landmark} onChange={(v) => setAddress({ ...address, landmark: v })} testid="addr-landmark" />
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="City" value={address.city} onChange={(v) => setAddress({ ...address, city: v })} testid="addr-city" />
                        <Field label="Pincode" value={address.pincode} onChange={(v) => setAddress({ ...address, pincode: v })} testid="addr-pincode" />
                      </div>
                      <Field label="Delivery Instructions (optional)" value={address.instructions} onChange={(v) => setAddress({ ...address, instructions: v })} testid="addr-instructions" />
                      {savedAddresses.length > 0 && <button onClick={() => setUseNewAddr(false)} className="text-sm text-muted-foreground hover:text-navy">← Choose a saved address</button>}
                    </div>
                  )}
                  <div data-testid="delivery-serviceability">
                    {notServiceable ? (
                      <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="delivery-warning">
                        <AlertTriangle size={13} className="shrink-0" /> Sorry, we don't deliver to pincode {pin} yet. Try a different address or choose Operating Kitchen (pickup).
                      </div>
                    ) : matchArea ? (
                      <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="delivery-ok">
                        <Check size={13} className="shrink-0" /> Delivering to {matchArea.city} · ETA {matchArea.eta}
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <AlertTriangle size={13} className="text-gold" /> Enter your delivery pincode to check serviceability.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-4 space-y-3" data-testid="takeaway-section">
                  <p className="text-sm text-muted-foreground">Pick up your feast fresh from our kitchen — <span className="text-navy font-semibold">₹0 delivery fee</span>. Select a kitchen:</p>
                  {kitchens.length === 0 && <div className="text-sm text-muted-foreground bg-beige/60 rounded-lg p-4">No operating kitchens are available for pickup right now.</div>}
                  {kitchens.map((k) => {
                    const sel = k.id === kitchenId;
                    return (
                      <button key={k.id} onClick={() => setKitchenId(k.id)} data-testid={`kitchen-${k.id}`}
                        className={`w-full text-left p-4 rounded-lg border transition-all ${sel ? "border-gold bg-gold/5" : "border-black/10 hover:border-navy/30"}`}>
                        <div className="flex items-start gap-3">
                          <Store size={18} className="text-gold mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <div className="font-medium text-navy">{k.name}</div>
                            <div className="text-sm text-muted-foreground">{k.address}{k.pincode ? ` - ${k.pincode}` : ""}</div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-navy">
                              <span className="flex items-center gap-1"><Clock size={12} className="text-gold" /> Ready in {k.ready_time}</span>
                              <span className="flex items-center gap-1"><Clock size={12} className="text-gold" /> {k.pickup_hours}</span>
                              <span className="text-muted-foreground">Min order {rupee(k.min_order)}</span>
                            </div>
                          </div>
                          {sel && <Check className="text-gold shrink-0" size={18} />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <button onClick={() => { if (notServiceable) { toast.error("We don't deliver to this pincode yet. Please choose a serviceable address or Operating Kitchen (pickup)."); return; } setStep(3); }} data-testid="to-summary-btn" className="mt-4 bg-navy text-cream font-semibold px-6 py-3 rounded-full hover:bg-navy-soft transition-colors active:scale-95">Continue to Summary</button>
            </Card>
          )}

          {step === 3 && (
            <Card title="Order Summary" testid="step-summary">
              <div className="space-y-3">
                {items.map((it) => (
                  <div key={it.lineId} className="flex justify-between text-sm border-b border-dashed border-black/10 pb-2">
                    <div><span className="font-medium text-navy">{it.name}</span> <span className="text-muted-foreground">× {it.quantity}</span>{it.variant && <span className="text-muted-foreground"> · {it.variant}</span>}</div>
                    <span className="text-navy">{rupee((it.unit_price + [...it.customizations, ...it.add_ons].reduce((s, o) => s + o.price, 0)) * it.quantity)}</span>
                  </div>
                ))}
              </div>
              {orderType === "takeaway" && selectedKitchen && (
                <div className="mt-4 bg-beige/60 rounded-lg p-3 text-sm" data-testid="summary-pickup">
                  <div className="font-semibold text-navy flex items-center gap-1.5"><Store size={14} className="text-gold" /> Pickup from {selectedKitchen.name}</div>
                  <div className="text-muted-foreground text-xs mt-0.5">{selectedKitchen.address} · Ready in {selectedKitchen.ready_time}</div>
                </div>
              )}
              <div className="mt-4" data-testid="special-instructions-block">
                <label className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><MessageSquare size={14} className="text-gold" /> Special Instructions / Add a Request</label>
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="preset-requests">
                  {PRESET_REQUESTS.map((label) => (
                    <button key={label} type="button" onClick={() => togglePreset(label)}
                      data-testid={`preset-${label.toLowerCase().replace(/\s+/g, "-")}`}
                      aria-pressed={presetActive(label)}
                      className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${presetActive(label) ? "bg-navy text-cream border-navy" : "bg-white text-navy border-black/15 hover:border-gold"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <textarea value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} rows={3} maxLength={500}
                  placeholder="e.g. Make it extra spicy, no onions, nut allergy, ring the bell..." data-testid="special-instructions-input"
                  className="w-full mt-2 border border-black/15 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold resize-none" />
                <div className="text-[11px] text-muted-foreground mt-1">Tap a chip for a quick request or type your own. Our kitchen will see this against your order.</div>
              </div>
              <button onClick={() => setStep(4)} data-testid="to-payment-btn" className="mt-4 bg-navy text-cream font-semibold px-6 py-3 rounded-full hover:bg-navy-soft transition-colors active:scale-95">Continue to Payment</button>
            </Card>
          )}

          {step === 4 && (
            <Card title="Payment Method" testid="step-payment">
              <button onClick={() => !paymentsStopped && setPayMethod("online")} disabled={paymentsStopped} data-testid="pay-online" className={`w-full flex items-center gap-3 p-4 rounded-lg border mb-3 ${paymentsStopped ? "border-black/10 opacity-50 cursor-not-allowed" : payMethod === "online" ? "border-gold bg-gold/5" : "border-black/10"}`}>
                <CreditCard className="text-navy" size={20} />
                <div className="text-left flex-1"><div className="font-medium text-navy">Pay with Razorpay</div><div className="text-xs text-muted-foreground">{paymentsStopped ? "Online payments are temporarily unavailable — please use Cash on Delivery" : "UPI, Cards & Net Banking — secure INR payment"}</div></div>
                {!paymentsStopped && payMethod === "online" && <Check className="text-gold" size={18} />}
              </button>
              <button onClick={() => setPayMethod("cod")} data-testid="pay-cod" className={`w-full flex items-center gap-3 p-4 rounded-lg border ${payMethod === "cod" ? "border-gold bg-gold/5" : "border-black/10"}`}>
                <Wallet className="text-navy" size={20} />
                <div className="text-left flex-1"><div className="font-medium text-navy">{orderType === "takeaway" ? "Pay at Pickup" : "Cash on Delivery"}</div><div className="text-xs text-muted-foreground">{orderType === "takeaway" ? "Pay when you collect your order" : "Pay when your feast arrives"}</div></div>
                {payMethod === "cod" && <Check className="text-gold" size={18} />}
              </button>
              <button onClick={placeOrder} disabled={placing || ordersStopped} data-testid="place-order-btn" className="w-full mt-5 btn-shine bg-gold text-navy font-semibold py-3.5 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed active:scale-95">
                {ordersStopped ? "Ordering Unavailable" : placing ? "Processing..." : payMethod === "online" ? `Pay ${rupee(total)}` : `Place Order · ${rupee(total)}`}
              </button>
            </Card>
          )}
        </div>

        <div className="lg:col-span-5 bg-white rounded-xl border border-black/5 shadow-sm p-6 h-fit lg:sticky lg:top-24">
          <h3 className="font-serif text-2xl text-navy mb-4">Bill Details</h3>
          <div className="flex gap-2 mb-4">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Coupon code" data-testid="coupon-input" className="flex-1 border border-black/15 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gold" />
            <button onClick={applyCoupon} data-testid="apply-coupon-btn" className="bg-navy text-cream text-sm font-semibold px-4 rounded-lg hover:bg-navy-soft transition-colors">Apply</button>
          </div>
          <div className="text-xs text-muted-foreground mb-3">Have a referral or welcome coupon? Apply it here.</div>
          {availablePoints > 0 && maxRedeem > 0 && (
            <button onClick={() => setRedeem(!redeem)} data-testid="redeem-points-toggle"
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg border mb-4 text-sm transition-colors ${redeem ? "border-gold bg-gold/5" : "border-black/10"}`}>
              <span className="flex items-center gap-2"><Sparkles size={15} className="text-gold" /> Use {maxRedeem} loyalty points</span>
              <span className={`w-4 h-4 rounded border flex items-center justify-center ${redeem ? "bg-gold border-gold" : "border-black/25"}`}>{redeem && <Check size={11} className="text-navy" />}</span>
            </button>
          )}
          <div className="space-y-2.5 text-sm">
            <Row label="Subtotal" value={rupee(subtotal)} />
            {discount > 0 && <Row label={`Discount (${applied.code})`} value={`- ${rupee(discount)}`} gold />}
            {pointsApplied > 0 && <Row label={`Loyalty (${pointsApplied} pts)`} value={`- ${rupee(pointsApplied)}`} gold />}
            <Row label="GST (5%)" value={rupee(tax)} />
            <Row label={orderType === "takeaway" ? "Pickup" : "Delivery Fee"} value={delivery === 0 ? "Free" : rupee(delivery)} />
          </div>
          <div className="gold-rule my-4" />
          <div className="flex justify-between font-semibold text-3xl text-navy font-serif"><span className="text-lg self-center">Total</span><span data-testid="checkout-total">{rupee(total)}</span></div>
          <div className="flex items-center gap-1.5 text-xs text-gold mt-3 bg-gold/10 rounded-lg px-3 py-2" data-testid="points-earn-hint"><Sparkles size={13} /> Earn {pointsEarn} points on this order</div>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children, testid }) {
  return <div className="bg-white rounded-xl border border-black/5 shadow-sm p-6 space-y-3" data-testid={testid}><h3 className="font-serif text-2xl text-navy">{title}</h3>{children}</div>;
}
function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full mt-1 border border-black/15 rounded-lg px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-gold" />
    </div>
  );
}
function Row({ label, value, gold }) {
  return <div className="flex justify-between text-muted-foreground"><span>{label}</span><span className={gold ? "text-gold font-medium" : "text-navy font-medium"}>{value}</span></div>;
}
